// src/cockpit4/components/CompetitorVisibilityPanel.tsx
// ============================================
// P1+ 竞品 AI 可见性对标：品牌 vs 竞品 在各 AI 平台的提及/首推对比
//   数据源：localStorage xeaos_ai_visibility（AiVisibilityMonitor 记录）
//   矩阵：行=品牌+竞品，列=AI平台，格=提及次数
// ============================================

import { useEffect, useMemo, useState } from "react";
import { Swords, RefreshCw } from "lucide-react";

type AiPlatform = "doubao" | "ernie" | "qwen" | "deepseek";
const PLATFORMS: { key: AiPlatform; name: string; color: string }[] = [
  { key: "doubao", name: "豆包", color: "#3B82F6" },
  { key: "ernie", name: "文心", color: "#EF4444" },
  { key: "qwen", name: "通义", color: "#8B5CF6" },
  { key: "deepseek", name: "DeepSeek", color: "#10B981" },
];

interface VisRecord { ai: AiPlatform; mentioned: boolean; firstPosition: boolean; top3: boolean; competitors: string[]; question: string; }
const COMP_KEY = "xeaos_ai_visibility.competitors";
const REC_KEY = "xeaos_ai_visibility";

const loadRecs = (): VisRecord[] => {
  try {
    const raw = localStorage.getItem(REC_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw);
    return Array.isArray(p?.records) ? p.records : [];
  } catch { return []; }
};
const loadComps = (): string[] => {
  try {
    const raw = localStorage.getItem(COMP_KEY);
    if (raw) { const a = JSON.parse(raw); if (Array.isArray(a) && a.length) return a; }
  } catch { /* ignore */ }
  return ["扣子", "Coze", "Dify", "影刀", "简道云", "文心智能体"];
};

export default function CompetitorVisibilityPanel() {
  const [recs, setRecs] = useState<VisRecord[]>(loadRecs);
  const [comps, setComps] = useState<string[]>(loadComps);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => { setRecs(loadRecs()); setComps(loadComps()); setTick((x) => x + 1); }, 3000);
    return () => clearInterval(t);
  }, []);

  // 行：品牌 + 竞品
  const rows = useMemo(() => ["__brand__", ...comps], [comps]);

  const matrix = useMemo(() => {
    // cell[r][p] = { total, mention, first }
    const m: Record<string, Record<string, { total: number; mention: number; first: number }>> = {};
    for (const r of rows) for (const p of PLATFORMS) {
      if (!m[r]) m[r] = {};
      m[r][p.key] = { total: 0, mention: 0, first: 0 };
    }
    for (const rec of recs) {
      const p = rec.ai;
      if (!PLATFORMS.find((x) => x.key === p)) continue;
      // 品牌
      m["__brand__"][p].total++;
      if (rec.mentioned) m["__brand__"][p].mention++;
      if (rec.firstPosition) m["__brand__"][p].first++;
      // 竞品
      for (const c of rec.competitors || []) {
        if (!m[c]) { m[c] = {}; for (const pp of PLATFORMS) m[c][pp.key] = { total: 0, mention: 0, first: 0 }; }
        m[c][p].total++; // 竞品出现即计
        m[c][p].mention++;
      }
    }
    return m;
  }, [recs, rows]);

  const totalFor = (r: string) => {
    let t = 0, m = 0;
    for (const p of PLATFORMS) { t += matrix[r]?.[p.key]?.total || 0; m += matrix[r]?.[p.key]?.mention || 0; }
    return { total: t, mention: m, rate: t ? Math.round((m / t) * 100) : 0 };
  };

  const brandTotal = totalFor("__brand__");

  return (
    <div className="space-y-2" key={tick}>
      <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
        <Swords size={11} className="text-amber-400" />
        竞品 AI 可见性对标：品牌 vs 竞品在各 AI 平台的提及对比
        <RefreshCw size={9} className="ml-auto text-gray-600 animate-pulse" />
      </div>

      {recs.length === 0 ? (
        <p className="text-[10px] text-gray-600 text-center py-4">暂无监测数据。先到「AI可见性」跑一轮诊断或录入查询记录，竞品出现会自动统计。</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[9px] font-mono">
            <thead>
              <tr>
                <th className="text-left px-2 py-1 text-gray-600">实体＼平台</th>
                {PLATFORMS.map((p) => <th key={p.key} className="px-2 py-1" style={{ color: p.color }}>{p.name}</th>)}
                <th className="px-2 py-1 text-gray-500">总提及率</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isBrand = r === "__brand__";
                const t = totalFor(r);
                return (
                  <tr key={r} className={isBrand ? "bg-cyan-500/[0.04]" : ""}>
                    <td className="px-2 py-1" style={{ color: isBrand ? "#22D3EE" : "#9aa7b5", fontWeight: isBrand ? 700 : 400 }}>
                      {isBrand ? "我方品牌" : r}
                    </td>
                    {PLATFORMS.map((p) => {
                      const c = matrix[r]?.[p.key];
                      const v = c && c.total ? Math.round((c.mention / c.total) * 100) : 0;
                      return (
                        <td key={p.key} className="px-2 py-1 text-center">
                          {c && c.total > 0 ? (
                            <span style={{ color: v >= 50 ? "#10B981" : v > 0 ? "#F59E0B" : "#6B7280" }}>{v}%<span className="text-gray-600">({c.mention}/{c.total})</span></span>
                          ) : <span className="text-gray-700">—</span>}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 text-center" style={{ color: t.rate >= 50 ? "#10B981" : "#F59E0B" }}>{t.rate}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[8px] font-mono text-gray-600">竞品列表在「AI可见性」→ 问题库/设置 中配置。竞品出现次数 = 该 AI 回答中竞品词被提及的次数。</p>
    </div>
  );
}
